import React, { useCallback, useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { startOfDay } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEv_list, fetchIv_list } from '../../../store';
import { fetchAndBuildDailyGeneration } from './services/dailyGenerationService';
import ReportToolbar from './components/ReportToolbar';
import ChartComponent from './components/ChartComponent';
import TableComponent from './components/TableComponent';
import {
  DAILY_PERIOD_OPTIONS,
  DAILY_PERIOD_RANGES,
} from './utils/reportPeriodRanges';
import './GenHourly.css';
import './GenDaily.css';

const MAX_DAILY_RANGE_MONTHS = 3;

const DailyGeneration = () => {
  const dispatch = useDispatch();
  const ivList = useSelector((state) => state.Iv_list);
  const Ev_list = useSelector((state) => state.Ev_list || {});

  const [viewMode, setViewMode] = useState('total');
  const [displayType, setDisplayType] = useState('chart');
  const [tempViewMode, setTempViewMode] = useState('total');
  const [tempDisplayType, setTempDisplayType] = useState('chart');

  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(startOfDay(new Date()));
  const [activePeriod, setActivePeriod] = useState('thisMonth');

  const [displayedData, setDisplayedData] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [lastSearchMode, setLastSearchMode] = useState('total');
  const [isLoading, setIsLoading] = useState(false);

  const totalCapacity = (ivList?.iv_capa || []).reduce((sum, capa) => sum + (capa || 0), 0);

  useEffect(() => {
    dispatch(fetchIv_list());
    dispatch(fetchEv_list());
  }, [dispatch]);

  const runSearch = useCallback(async (searchStart = startDate, searchEnd = endDate) => {
    try {
      setIsLoading(true);
      const { displayed, csv } = await fetchAndBuildDailyGeneration({
        start: searchStart,
        end: searchEnd,
        mode: tempViewMode,
        ivList,
        Ev_list,
        totalCapacity,
      });

      setDisplayedData(displayed);
      setCsvData(csv);
      setLastSearchMode(tempViewMode);
      setViewMode(tempViewMode);
      setDisplayType(tempDisplayType);
    } catch (error) {
      console.error(error);
      alert(error.message || '데이터를 가져오는 중 오류가 발생했습니다.');
      setDisplayedData(null);
      setCsvData([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, tempViewMode, tempDisplayType, ivList, Ev_list, totalCapacity]);

  const handleSearch = useCallback(() => {
    runSearch(startDate, endDate);
  }, [runSearch, startDate, endDate]);

  const applyPeriod = useCallback(async (periodKey) => {
    const range = DAILY_PERIOD_RANGES[periodKey]();
    setStartDate(range.start);
    setEndDate(range.end);
    setActivePeriod(range.key);
    await runSearch(range.start, range.end);
  }, [runSearch]);

  return (
    <div className="gen-hourly-page gen-daily-page">
      <ReportToolbar
        periodOptions={DAILY_PERIOD_OPTIONS}
        activePeriod={activePeriod}
        onPeriodClick={applyPeriod}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        pickerMode="date"
        maxRangeMonths={MAX_DAILY_RANGE_MONTHS}
        rangeHint="조회 기간은 최대 3개월까지 선택할 수 있습니다."
        tempViewMode={tempViewMode}
        setTempViewMode={setTempViewMode}
        tempDisplayType={tempDisplayType}
        setTempDisplayType={setTempDisplayType}
        handleSearch={handleSearch}
        isLoading={isLoading}
        csvData={csvData}
        csvFilename="일일발전량.csv"
        onDateChange={() => setActivePeriod(null)}
      />

      {displayType === 'chart' ? (
        <ChartComponent displayedData={displayedData} lastSearchMode={lastSearchMode} />
      ) : (
        <TableComponent displayedData={displayedData} viewMode={viewMode} ivList={ivList} />
      )}
    </div>
  );
};

export default DailyGeneration;
